<template>
  <aside class="flex h-full min-h-0 flex-col border-e bg-muted/50 px-4 py-5">
    <!-- Logo (kept unchanged) -->
    <NuxtLink to="/dashboard" class="block px-2 pb-[22px]" @click="handleLinkClick">
      <ClientOnly>
        <img
          v-if="$colorMode.value === 'dark'"
          alt="DownSub"
          src="/logo-light.png"
          class="w-[150px]"
        />
        <img v-else alt="DownSub" src="/logo.png" class="w-[150px]" />
      </ClientOnly>
    </NuxtLink>

    <nav
      ref="navRef"
      :aria-label="$t('common.mainNavigation')"
      class="scrollbar-scroll relative -me-1.5 flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden pb-5 pe-1.5"
    >
      <!-- Highlight that slides to the active link (positioned from offsetTop/offsetLeft) -->
      <span
        ref="activePillRef"
        class="pointer-events-none absolute left-0 top-0 rounded-md bg-primary/10 opacity-0"
        aria-hidden="true"
      ></span>
      <template v-for="(route, index) in routes" :key="index">
        <!-- Section labels matching the design -->
        <span v-if="route.label === 'tools'" :class="labelClass">{{ $t("common.navTools") }}</span>
        <span v-if="route.label === 'apiManagement'" :class="labelClass">{{ $t("common.navAccount") }}</span>
        <span v-if="route.label === 'admin'" :class="labelClass">{{ $t("common.navAdmin") }}</span>

        <!-- Locked single link: guests get the login popup -->
        <button
          v-if="!route.submenu && isLocked(route)"
          type="button"
          :class="linkClass"
          @click="requireLogin"
        >
          <Icon :name="route.icon" class="h-5 w-5 flex-none" />
          <span>{{ $t(`common.${route.label}`) }}</span>
          <Icon name="lucide:lock" class="ms-auto h-[15px] w-[15px] opacity-70" aria-hidden="true" />
          <span class="sr-only">{{ $t("authModal.title") }}</span>
        </button>

        <!-- Single link -->
        <NuxtLink
          v-else-if="!route.submenu"
          :to="route.link"
          :data-active="currentRoute.path === route.link || undefined"
          :class="[linkClass, currentRoute.path === route.link ? activeClass : '']"
          @click="handleLinkClick"
        >
          <Icon :name="route.icon" class="h-5 w-5 flex-none" />
          <span>{{ $t(`common.${route.label}`) }}</span>
        </NuxtLink>

        <!-- Expandable submenu category -->
        <template v-else>
          <button
            type="button"
            :class="linkClass"
            :aria-expanded="!!expandedCategories[route.label]"
            :aria-controls="`${groupIdPrefix}-${route.label}`"
            @click="toggleCategory(route.label)"
          >
            <Icon :name="route.icon" class="h-5 w-5 flex-none" />
            <span>{{ $t(`common.${route.label}`) }}</span>
            <!-- One chevron that turns to point down; -rotate-90 under RTL because the icon is mirrored there -->
            <Icon
              name="lucide:chevron-right"
              class="ms-auto h-4 w-4 opacity-60 transition-transform duration-200 rtl:-scale-x-100 motion-reduce:transition-none"
              :class="expandedCategories[route.label] ? 'rotate-90 rtl:-rotate-90' : ''"
              aria-hidden="true"
            />
          </button>
          <!-- flex-none: with overflow hidden during the animation, a shrinkable flex item
               would be squeezed to the sidebar height and jump back at the end -->
          <Transition :css="false" @enter="onGroupEnter" @leave="onGroupLeave">
          <div
            v-show="expandedCategories[route.label]"
            :id="`${groupIdPrefix}-${route.label}`"
            class="ms-[18px] flex flex-none flex-col gap-0.5"
          >
            <template v-for="child in route.children" :key="child.link">
              <button
                v-if="isLocked(child)"
                type="button"
                :class="[linkClass, subLinkClass]"
                @click="requireLogin"
              >
                <Icon :name="child.icon" class="h-5 w-5 flex-none" />
                <span>{{ $t(`common.${child.label}`) }}</span>
                <Icon name="lucide:lock" class="ms-auto h-[15px] w-[15px] opacity-70" aria-hidden="true" />
                <span class="sr-only">{{ $t("authModal.title") }}</span>
              </button>
              <NuxtLink
                v-else
                :to="child.link"
                :target="isExternal(child.link) ? '_blank' : undefined"
                :rel="isExternal(child.link) ? 'noopener noreferrer' : undefined"
                :data-active="currentRoute.path === child.link || undefined"
                :class="[linkClass, subLinkClass, currentRoute.path === child.link ? activeClass : '']"
                @click="handleLinkClick"
              >
                <Icon :name="child.icon" class="h-5 w-5 flex-none" />
                <span>{{ $t(`common.${child.label}`) }}</span>
              </NuxtLink>
            </template>
          </div>
          </Transition>
        </template>
      </template>
    </nav>

    <!-- Credits / upgrade card (compact - details live on the dashboard) -->
    <div class="mt-auto rounded-lg border bg-card p-3.5">
      <template v-if="user && typeof user.isPro === 'boolean'">
        <div class="flex items-baseline justify-between gap-2">
          <span class="text-[12.5px] text-subtle-foreground">{{ user.isPro ? $t("common.creditsRemainingShort") : $t("common.planLabel") }}</span>
          <!-- Mono only for the number (Mono-Means-Data); the "Free" plan name stays sans -->
          <b class="text-[15px]" :class="{ 'font-mono': user.isPro }">{{ user.isPro ? formatNumber(creditsRemaining, locale) : $t("common.free") }}</b>
          <Popover v-if="user.isPro">
            <PopoverTrigger asChild>
              <Button variant="ghost" class="relative ms-1 h-4 w-4 p-0 after:absolute after:-inset-[14px] after:content-[''] [@media(pointer:coarse)]:h-4" :aria-label="$t('common.credits_info')">
                <Icon name="lucide:info" class="h-4 w-4" aria-hidden="true" />
              </Button>
            </PopoverTrigger>
            <PopoverContent class="w-72 p-4 text-sm">
              <div class="space-y-3">
                <div class="border-b pb-2">
                  <h4 class="font-medium mb-1">{{ $t("common.credits_info") }}</h4>
                  <div v-if="user?.planState === 'premium'" class="flex justify-between">
                    <span>{{ $t("common.monthly_usage") }}:</span>
                    <span class="font-medium" :class="user?.monthly_limit === 0 ? 'text-destructive' : ''">
                      {{ formatNumber(usageUsed, locale) }} / {{ formatNumber(MONTHLY_LIMIT, locale) }}
                    </span>
                  </div>
                  <div v-if="user?.bonus_limit" class="flex justify-between mt-1">
                    <span>{{ $t("common.bonus_credits") }}:</span>
                    <span class="font-medium">{{ formatNumber(user.bonus_limit, locale) }}</span>
                  </div>
                </div>
                <div v-if="user?.planState === 'premium' && user?.next_reset_credit" class="flex items-center gap-2">
                  <Icon name="lucide:refresh-cw" class="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div class="text-xs text-muted-foreground">{{ $t("common.credits_reset_on") }}:</div>
                    <div class="font-medium">{{ formatDateTime(user.next_reset_credit, locale) }}</div>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <Button
          v-if="user.isPro && user.monthly_limit === 0 && user.bonus_limit === 0"
          size="sm"
          class="mt-2.5 w-full font-semibold"
          @click="proModal.onOpen('payPerUse')"
        >
          {{ $t("buttons.purchaseCredits") }}
        </Button>
        <Button
          v-else-if="!user.isPro"
          size="sm"
          class="mt-2.5 w-full font-semibold"
          @click="proModal.onOpen('subscription')"
        >
          {{ user?.canUseTrial !== false ? $t("buttons.startFreeTrial") : $t("buttons.upgrade") }}
        </Button>
      </template>

      <!-- Link styled as a button (local Button has no as-child) -->
      <NuxtLink
        v-else-if="!supabaseUser"
        to="/auth"
        :class="[buttonVariants({ size: 'sm' }), 'w-full font-semibold']"
        @click="handleLinkClick"
      >
        {{ $t("authModal.loginButton") }}
      </NuxtLink>
      <Loader v-else :processing="false" />
    </div>
  </aside>
</template>

<script setup lang="ts">
import { MONTHLY_LIMIT } from "@/utils";
import { useProModal } from "@/store/useProModal";
import { dashboardRoutes } from "@/utils";
import { formatDateTime, formatNumber } from "@/utils/tools";
import { buttonVariants } from "@/components/ui/button";
import { animateHeight, gsap } from "~/utils/motion";

// Shared nav-link classes (replace the legacy ds-dash .side-link)
// relative: links paint above the absolutely positioned active pill
const linkClass =
  "relative flex w-full cursor-pointer items-center gap-[11px] rounded-md px-[11px] py-2.5 text-start text-[14.5px] [@media(pointer:coarse)]:min-h-11 font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground";
const subLinkClass = "py-[9px] text-[13.8px]";
// Background comes from the sliding pill once it is placed; until then the link paints it.
const activeTextClass = "!text-primary font-semibold";
const labelClass =
  "px-2.5 pb-2 pt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-subtle-foreground";

const proModal = useProModal();
const currentRoute = useRoute();
const { locale } = useI18n();
const expandedCategories = ref<Record<string, boolean>>({
  tools: true,
  apiManagement: false,
  admin: false,
});

// Unique per instance: desktop and mobile sidebars can be in the DOM together.
// Deterministic (not useId) so SSR and hydration agree; only the mobile sheet provides closeMobileSidebar.
const groupIdPrefix = inject("closeMobileSidebar", null) ? "sidebar-mobile" : "sidebar";

const toggleCategory = (label: string) => {
  expandedCategories.value[label] = !expandedCategories.value[label];
};

// ---- Motion: sliding active pill + smooth submenu (instant under reduced motion) ----
const navRef = ref<HTMLElement | null>(null);
const { pill: activePillRef, ready: pillReady, place: placePill } = useSlidingHighlight(
  navRef,
  "[data-active]",
  // Route changes slide the pill; labels also change width with the locale.
  () => [currentRoute.path, locale.value]
);
// Until the pill is placed (SSR, first paint) the active link paints its own background.
const activeClass = computed(() => (pillReady.value ? activeTextClass : `bg-primary/10 ${activeTextClass}`));

// Submenu open/close. A pill on one of the group's own links would float over the
// clipped part, so that link paints its own highlight meanwhile (set on the DOM: a
// reactive change would re-render mid-<Transition> and cut it short); a pill on a
// link below the group just follows it.
const animateGroup = (el: Element, open: boolean, done: () => void) => {
  const activeLink = el.querySelector("[data-active]");
  activeLink?.classList.add("bg-primary/10");
  if (activeLink && activePillRef.value) gsap.set(activePillRef.value, { opacity: 0, overwrite: true });
  animateHeight(el, open, {
    onUpdate: () => !activeLink && placePill(),
    onComplete: () => {
      done();
      activeLink?.classList.remove("bg-primary/10");
      placePill();
    },
  });
};
const onGroupEnter = (el: Element, done: () => void) => animateGroup(el, true, done);
const onGroupLeave = (el: Element, done: () => void) => animateGroup(el, false, done);

// External links (e.g. Chrome Web Store) open a new tab instead of navigating in-app
const isExternal = (link?: string) => !!link && /^https?:\/\//.test(link);

const { user } = useUser();
const supabaseUser = useSupabaseUser();
const { requireLogin } = useAuthGate();

type DashboardRoute = {
  label: string;
  icon: string;
  link?: string;
  color?: string;
  submenu?: boolean;
  requiresAuth?: boolean;
  adminOnly?: boolean;
  children?: DashboardRoute[];
};

// App admin from Supabase Auth app_metadata (mirrors server isSupabaseAppAdmin).
const isAdmin = computed(() => {
  const meta = (supabaseUser.value?.app_metadata ?? {}) as Record<string, unknown>;
  return meta.isAdmin === true || meta.role === "admin";
});

// Hide admin-only groups/children from non-admin users.
const routes = computed<DashboardRoute[]>(() =>
  (dashboardRoutes as DashboardRoute[])
    .filter((route) => !route.adminOnly || isAdmin.value)
    .map((route) =>
      route.children
        ? { ...route, children: route.children.filter((child) => !child.adminOnly || isAdmin.value) }
        : route
    )
);

// Guest + auth-required route => show lock & open login popup
const isLocked = (route: { requiresAuth?: boolean }) =>
  !supabaseUser.value && !!route.requiresAuth;

const creditsRemaining = computed(() => {
  if (!user.value) return 0;
  return (user.value.monthly_limit || 0) + (user.value.bonus_limit || 0);
});

const usageUsed = computed(() =>
  MONTHLY_LIMIT - (user.value ? user.value.monthly_limit : 0)
);

// Inject closeSidebar function from MobileSidebar
const closeMobileSidebar = inject("closeMobileSidebar", () => {});

const handleLinkClick = () => {
  closeMobileSidebar();
};
</script>

<style scoped></style>
